import type { ProductToken } from '@sleed/bizcommerce-api';
import { toGreekCaps } from '~/lib/i18n/toGreekCaps';

/**
 * Products grid row view-model. Derived from the `getAllGrid` ProductToken projection
 * (abbreviated wire fields: cd=Code, nm=Name, iv=IsVisible, feat=IsFeatured, ptype=ProductType,
 * ast=AvailabilityStatus, img1=ImageUrl_1, barcodes=Barcode, cnm=CategoryNames, qty=Quantity,
 * cprc=CalculatedPrice, cprcbd=CalculatedPriceBD, cdsc=CalculatedDiscountPercentage).
 *
 * `stockTone` is UI-only (derived from qty against the low-stock threshold).
 */
export type StockTone = 'ok' | 'low' | 'out';

export interface ProductGridRow {
	id: number;
	name: string;
	/** SKU — the product Code. */
	sku: string;
	barcode: string | null;
	/**
	 * Distinct category names (CategoryNames), de-duplicated case-insensitively and
	 * order-preserving. The `getAllGrid` `cnm` field is a STRING_AGG that fans out
	 * duplicate ProductCategory rows, so the raw value contains repeats — this is the
	 * cleaned list the grid renders.
	 */
	categoryNames: string[];
	/** First category segment, shown in the name meta line. */
	categoryLead: string | null;
	/** Localised product-type label key suffix is resolved at render. */
	productType: number | null;
	imageUrl: string | null;
	quantity: number;
	stockTone: StockTone;
	/** Pre-discount price (CalculatedPriceBD) — null when there is no strike-through. */
	preDiscountPrice: number | null;
	/** Discount percentage (CalculatedDiscountPercentage) — null when none. */
	discountPercent: number | null;
	/** Final retail price (CalculatedPrice). */
	retailPrice: number | null;
	isVisible: boolean;
	isFeatured: boolean;
	availabilityStatus: number | null;
	// Extra fields backing the default-hidden columns ported from the legacy admin grid.
	// Optional so the typed demo rows below don't have to set them.
	subTitle?: string | null;
	baseName?: string | null;
	altCode?: string | null;
	groupCode?: string | null;
	erpCode?: string | null;
	manufacturerName?: string | null;
	subTypeName?: string | null;
	availabilityName?: string | null;
	hasDiscount?: boolean;
	releaseDate?: string | null;
	sortIndex?: number | null;
}

/** Low-stock threshold: at/below this (but > 0) renders the amber "low" pill. */
export const LOW_STOCK_THRESHOLD = 3;

export function stockToneOf(quantity: number): StockTone {
	if (quantity <= 0) return 'out';
	if (quantity <= LOW_STOCK_THRESHOLD) return 'low';
	return 'ok';
}

/**
 * Splits a raw `cnm` STRING_AGG value (comma-joined category names) into a distinct,
 * order-preserving list. The aggregate fans out duplicate ProductCategory join rows so
 * the same path appears multiple times (e.g. "A, A, B, C, A, A, B, C") — we keep the
 * first occurrence of each name and drop the rest. Matching is accent- AND case-insensitive
 * via `toGreekCaps` (strips tonos + uppercases), so "Παιχνίδια" and "ΠΑΙΧΝΙΔΙΑ" collapse to one.
 */
export function distinctCategoryNames(cnm: string | null | undefined): string[] {
	if (!cnm) return [];
	const seen = new Set<string>();
	const out: string[] = [];
	for (const raw of cnm.split(',')) {
		const name = raw.trim();
		if (!name) continue;
		const key = toGreekCaps(name);
		if (seen.has(key)) continue;
		seen.add(key);
		out.push(name);
	}
	return out;
}

/**
 * Maps a `getAllGrid` ProductToken (abbreviated fields) to the grid view-model.
 * The page uses the live hook; this mapper is shared by the hook and the typed fixtures below.
 */
export function tokenToProductRow(token: ProductToken): ProductGridRow {
	const quantity = token.stockQuantity ?? 0;
	const categoryNames = distinctCategoryNames(token.cnm);
	const categoryLead = categoryNames[0] ?? null;

	return {
		id: token.id ?? 0,
		name: token.nm ?? '',
		sku: token.cd ?? '',
		barcode: token.barcodes ?? null,
		categoryNames,
		categoryLead,
		productType: token.ptype ?? null,
		imageUrl: token.img1 ?? null,
		quantity,
		stockTone: stockToneOf(quantity),
		preDiscountPrice: token.cprcbd ?? null,
		discountPercent: token.cdsc ?? null,
		retailPrice: token.cprc ?? null,
		isVisible: token.iv ?? true,
		isFeatured: token.feat ?? false,
		availabilityStatus: token.availabilityStatus ?? null,
		subTitle: token.sub_title ?? null,
		baseName: token.bnm ?? null,
		altCode: token.altcd ?? null,
		groupCode: token.grpcd ?? null,
		erpCode: token.erpcd ?? null,
		manufacturerName: token.mfnm ?? null,
		subTypeName: token.subtnm ?? null,
		availabilityName: token.availabilityStatusName ?? null,
		hasDiscount: token.hdsc ?? false,
		releaseDate: token.release != null ? String(token.release) : null,
		sortIndex: token.si ?? null
	};
}

/**
 * Typed reference rows for dev/offline + stories. The page itself uses the REAL
 * `getAllGrid`/`getStatusCounts` hooks — these mirror the approved HTML sample rows.
 */
export const DEMO_PRODUCT_ROWS: ProductGridRow[] = [
	{
		id: 1,
		name: 'Έξυπνα μαθηματικά παιχνίδια',
		sku: '2760-4648',
		barcode: '9786180324...',
		categoryNames: ['ΔΗΜΙΟΥΡΓΙΚΗ ΑΠΑΣΧΟΛΗΣΗ', 'Παιχνίδια'],
		categoryLead: 'ΔΗΜΙΟΥΡΓΙΚΗ ΑΠΑΣΧΟΛΗΣΗ',
		productType: 1,
		imageUrl: null,
		quantity: 3,
		stockTone: 'low',
		preDiscountPrice: null,
		discountPercent: null,
		retailPrice: 6.15,
		isVisible: true,
		isFeatured: false,
		availabilityStatus: 1
	},
	{
		id: 3,
		name: 'Αρχαία Ελληνικά Γ’ Λυκείου (πρώτος τόμος) // Φάκελος Υλικού',
		sku: '3629-0111',
		barcode: '9786182290...',
		categoryNames: ['ΣΧΟΛΙΚΑ ΒΟΗΘΗΜΑΤΑ', 'Λυκείου'],
		categoryLead: 'ΣΧΟΛΙΚΑ ΒΟΗΘΗΜΑΤΑ',
		productType: 0,
		imageUrl: null,
		quantity: 0,
		stockTone: 'out',
		preDiscountPrice: 22.3,
		discountPercent: 25,
		retailPrice: 16.73,
		isVisible: true,
		isFeatured: true,
		availabilityStatus: 3
	},
	{
		id: 7,
		name: 'Γιόγκα: Η Πορεία της Συνείδησης',
		sku: '4421-1107',
		barcode: '9786180412...',
		categoryNames: ['ΑΥΤΟΒΕΛΤΙΩΣΗ - ΣΩΜΑ ΚΑΙ ΠΝΕΥΜΑ'],
		categoryLead: 'ΑΥΤΟΒΕΛΤΙΩΣΗ - ΣΩΜΑ ΚΑΙ ΠΝΕΥΜΑ',
		productType: 0,
		imageUrl: null,
		quantity: 1,
		stockTone: 'low',
		preDiscountPrice: null,
		discountPercent: null,
		retailPrice: 14.4,
		isVisible: false,
		isFeatured: false,
		availabilityStatus: 1
	}
];
